import { AxiosError } from 'axios';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { changeContractYearlyPlan } from 'services/contract';
import { loadSoftwaresAndServices } from 'services/software';
import { Contract } from 'shared/models/contract';
import { Plan } from 'shared/models/plan';
import { Service } from 'shared/models/service';
import { Software } from 'shared/models/software';
import { Periodicity } from 'shared/types/periodicity';
import { useFinancialContext } from '../../../FinancialContext';
import ChangePlanModal from './ChangePlanModal';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose(): void;
  contract?: Contract;
}

interface ChangePlanModalContextData {
  step: number;
  nextStep(): void;
  previousStep(): void;
  selectedPlan: number;
  setSelectedPlan(p: number): void;
  contract?: Contract;
  plan?: Plan;
  periodicity: Periodicity;
  setPeriodicity(p: Periodicity): void;
  loading: boolean;
  setLoading(b: boolean): void;
  handleSubmit(): void;
  setStep(n: number): void;
  availableServices: Service[];
}

const Context = createContext<ChangePlanModalContextData>(
  {} as ChangePlanModalContextData,
);

export const useChangePlanModalContext = () => useContext(Context);

const ChangePlanModalContext: React.FC<ChangePlanModalProps> = ({
  contract,
  isOpen,
  onClose,
}) => {
  const { reloadContracts } = useFinancialContext();

  const [step, setStep] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<number>(-1);

  const [loading, setLoading] = useState(false);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);

  const [periodicity, setPeriodicity] = useState<Periodicity>('yearly');

  const nextStep = () => setStep(step + 1);
  const previousStep = () => setStep(step - 1);

  const plan = useMemo(
    () => contract?.products.find((product) => product.isLicense)?.plan,
    [contract],
  );

  const loadAvailableServices = async () => {
    try {
      setLoading(true);
      const response = await loadSoftwaresAndServices();
      const softwares =
        response.data?.results?.map((software) => new Software(software)) ?? [];

      const yearlyLicenseServices: Service[] = [];
      softwares.forEach((software) => {
        software.services.forEach((service) => {
          if (service.periodicity === 'yearly' && service.is_license === 1) {
            yearlyLicenseServices.push(service);
          }
        });
      });

      // Ordena os serviços alfabeticamente em português brasileiro
      const sortedServices = yearlyLicenseServices.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR'),
      );

      setAvailableServices(sortedServices);
    } catch (error) {
      console.error('Error loading available services:', error);
      toast.error('Falha ao carregar os planos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!contract?.id || !contract?.licensePlan?.id) {
      toast.error('Falha ao trocar o plano. Contrato ou plano inválido');
      return;
    }

    setLoading(true);

    changeContractYearlyPlan(
      contract.id,
      contract.licensePlan.id,
      selectedPlan,
      periodicity,
    )
      .then(() => {
        toast.success('Plano trocado com sucesso');
        reloadContracts(true);
        onClose();
      })
      .catch((e: AxiosError) => {
        toast.error('Falha ao trocar o plano');
        console.error(
          'Falha ao trocar o plano',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setSelectedPlan(-1);
  }, [periodicity]);

  useEffect(() => {
    setStep(0);
    setSelectedPlan(-1);
    setPeriodicity(plan?.periodicity ?? 'yearly');
  }, [plan]);

  useEffect(() => {
    if (isOpen) {
      loadAvailableServices();
    }
  }, [isOpen]);

  const data: ChangePlanModalContextData = {
    step,
    nextStep,
    previousStep,
    selectedPlan,
    setSelectedPlan,
    contract,
    plan,
    periodicity,
    setPeriodicity,
    loading,
    setLoading,
    handleSubmit,
    setStep,
    availableServices,
  };

  return (
    <Context.Provider value={data}>
      <ChangePlanModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default ChangePlanModalContext;
